import React, {memo, ReactElement, useEffect, useMemo, useRef, useState} from 'react'
import styles from './UIGameLog.module.scss'
import {GameLogData} from '@colonist/game-states'
import {createFeedMessageDataForGameLogData} from '../UIGameFeedMessage/FeedMessageDataLogs'
import {UIGameFeedMessage} from '../UIGameFeedMessage/UIGameFeedMessage'
import {useUIGameStateContext} from '../../../contexts/UIGameStateContext'
import {GameValidator, PlayerValidator} from '@colonist/game-mechanic-validators'
import {UIGameFeedScrollContainer} from '../UIGameFeedScrollContainer/UIGameFeedScrollContainer'
import {cloneDeep} from 'lodash-es'
import {useUIGameReplayContext} from '../../../contexts/UIGameReplayContext'
import {useUIGamePlayersContext} from '../../../contexts/UIGamePlayersContext'

interface UIGameLogFeedMessageProps {
    playerValidator?: PlayerValidator | undefined
    gameValidator: GameValidator
    gameLogData: GameLogData
    opacity: number
    onClick?: ((logIndex: number) => void) | undefined
    logIndex: number
}

const UIGameLogFeedMessage = memo(function UIGameLogFeedMessage({
    playerValidator,
    gameValidator,
    gameLogData,
    opacity = 1,
    onClick,
    logIndex,
}: UIGameLogFeedMessageProps) {
    const feedMessageData = createFeedMessageDataForGameLogData(gameValidator, gameLogData, playerValidator)

    return (
        <UIGameFeedMessage
            message={feedMessageData}
            onClick={onClick}
            opacity={opacity}
            logIndex={logIndex}
        />
    )
}, (prev, next) => prev.opacity === next.opacity)

// TODO remove once UIReplayController is converted to ReactJS
let globalAddNewLog: undefined | ((logIndex: number, gameLogData: GameLogData) => void)
export function addNewLog(logIndex: number, gameLogData: GameLogData) {
    if(globalAddNewLog) globalAddNewLog(logIndex, gameLogData)
}

export function UIGameLog({
    isLogVisible,
    onClickLog,
    gameValidator,
}: {
    isLogVisible: boolean
    onClickLog?: (logIndex: number) => void
    gameValidator: GameValidator
}) {
    const {gameState} = useUIGameStateContext()
    const {focusLogIndex, currentPlayerPerspective} = useUIGameReplayContext()
    const {getPlayerValidator} = useUIGamePlayersContext()

    const [logs, setLogs] = useState<Record<string, {
        data: GameLogData
        playerValidator?: PlayerValidator | undefined
    }>>({})

    globalAddNewLog = (logIndex, gameLogData) => {
        const newLogData: {
            data: GameLogData
            playerValidator?: PlayerValidator | undefined
        } = {
            data: gameLogData,
        }
        if(gameLogData.from != null) {
            const playerValidator = getPlayerValidator(gameLogData.from)
            // User's avatar can be bot avatar or their actual avatar. cloneDeep to keep the avatar at the state of that particular log
            newLogData.playerValidator = cloneDeep(playerValidator)
        }
        setLogs((prevLogs) => ({
            ...prevLogs,
            [logIndex]: newLogData,
        }))
    }

    // Initial logs from gameState when UIGame is initialized in UIWGameManager
    useEffect(() => {
        if(gameState?.gameLogState != null) {
            const logsFromState: Record<string, {
                data: GameLogData
                playerValidator?: PlayerValidator | undefined
            }> = {}
            for(const key in gameState.gameLogState) {
                const logData = gameState.gameLogState[key]
                const logItem: {
                    data: GameLogData
                    playerValidator?: PlayerValidator | undefined
                } = {
                    data: logData,
                }
                if(logData.from != null) {
                    const playerValidator = getPlayerValidator(logData.from)
                    logItem.playerValidator = cloneDeep(playerValidator)
                }
                logsFromState[key] = logItem
            }
            setLogs(logsFromState)
        }
    }, [currentPlayerPerspective])

    const logIndexToChildIndex = useRef(new Map<number, number>())
    const messages = useMemo(() => {
        logIndexToChildIndex.current.clear()
        const msgs: ReactElement[] = []

        for(const key in logs) {
            const logIndex = Number(key)
            const gameLogData = logs[key].data
            const playerValidator = logs[key].playerValidator
            const opacity = focusLogIndex !== null && logIndex > focusLogIndex ? 0.3 : 1
            const message = <UIGameLogFeedMessage
                gameLogData={gameLogData}
                gameValidator={gameValidator}
                key={key}
                logIndex={logIndex}
                onClick={onClickLog}
                opacity={opacity}
                playerValidator={playerValidator}
            />

            logIndexToChildIndex.current.set(logIndex, msgs.length)
            msgs.push(message)
        }

        return msgs
    }, [logs, focusLogIndex, onClickLog])

    const scrollToChildIndex = useMemo(() => {
        if(focusLogIndex === null) return null

        const childIndex = logIndexToChildIndex.current.get(focusLogIndex)
        if(childIndex === undefined) return null

        return childIndex
    }, [focusLogIndex, currentPlayerPerspective])

    if(!isLogVisible) return null

    return (
        <div className={styles.container}>
            <UIGameFeedScrollContainer scrollToChildIndex={scrollToChildIndex}>
                {messages}
            </UIGameFeedScrollContainer>
        </div>
    )
}
